import type { AdminAccessRequest } from "@/lib/api/admin-access-requests";

export const ADMIN_REQUESTS_PAGE_SIZE = 10;

export type AdminRequestSortKey =
  | "applicant"
  | "business"
  | "type"
  | "volume"
  | "status"
  | "submitted";

export type AdminRequestSortDir = "asc" | "desc";

const VOLUME_RANK: Record<string, number> = {
  under_1k: 0,
  "1k_10k": 1,
  "10k_100k": 2,
  "100k_plus": 3,
};

const STATUS_RANK: Record<string, number> = {
  pending: 0,
  approved: 1,
  rejected: 2,
};

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function sortValue(
  request: AdminAccessRequest,
  key: AdminRequestSortKey,
): string | number {
  switch (key) {
    case "applicant":
      return request.contact_name;
    case "business":
      return request.company_name;
    case "type":
      return request.requester_type;
    case "volume":
      return VOLUME_RANK[request.expected_daily_volume] ?? 99;
    case "status":
      return STATUS_RANK[request.status] ?? 99;
    case "submitted":
      return new Date(request.created_at).getTime() || 0;
    default:
      return 0;
  }
}

export function sortAdminAccessRequests(
  requests: AdminAccessRequest[],
  key: AdminRequestSortKey,
  dir: AdminRequestSortDir,
): AdminAccessRequest[] {
  const factor = dir === "asc" ? 1 : -1;
  return [...requests].sort((left, right) => {
    const a = sortValue(left, key);
    const b = sortValue(right, key);
    if (typeof a === "number" && typeof b === "number") {
      return (a - b) * factor;
    }
    return compareText(String(a), String(b)) * factor;
  });
}

export function paginateAdminAccessRequests<T>(
  items: T[],
  page: number,
  pageSize: number = ADMIN_REQUESTS_PAGE_SIZE,
): { pageItems: T[]; pageCount: number; safePage: number } {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    pageCount,
    safePage,
  };
}

export function adminResultRangeLabel(
  total: number,
  page: number,
  pageSize: number = ADMIN_REQUESTS_PAGE_SIZE,
): string {
  if (total === 0) {
    return "Showing 0 requests";
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  if (start === end) {
    return `Showing ${start} of ${total}`;
  }
  return `Showing ${start}–${end} of ${total}`;
}

/** Stable label for sort headers / aria. */
export function adminSortColumnLabel(key: AdminRequestSortKey): string {
  switch (key) {
    case "applicant":
      return "Applicant";
    case "business":
      return "Business";
    case "type":
      return "Type";
    case "volume":
      return "Volume / day";
    case "status":
      return "Status";
    case "submitted":
      return "Submitted";
    default:
      return key;
  }
}
